/**
 * RouteCorridorsView - Full-page view for route corridors management
 *
 * Uses shared flush PageShell: no parent Paper; borders only on corridor cards.
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Add, Refresh, Search } from "@mui/icons-material";
import { message, Modal } from "antd";
import { Form } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getRouteCorridors,
  createRouteCorridor,
  updateRouteCorridor,
  deleteRouteCorridor,
} from "../services/routeCorridorService";
import RouteCorridorList from "../components/RouteCorridorList";
import RouteCorridorForm from "../components/RouteCorridorForm";
import RequireAdmin from "../../../components/ProtectedRoute";
import { useDeviceType } from "../../leads/hooks/useDeviceType";
import {
  PageShell,
  PageHeader,
  PageMetricStrip,
  PageFilters,
} from "../../../shared/components/pageShell";

/**
 * RouteCorridorsView Component
 */
const RouteCorridorsView = () => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const [form] = Form.useForm();
  const [routeCorridors, setRouteCorridors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editRouteCorridor, setEditRouteCorridor] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [filters, setFilters] = useState({
    active: undefined,
    search: "",
  });

  const fetchRouteCorridors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getRouteCorridors(filters);
      if (result.success) {
        setRouteCorridors(result.data || []);
      } else {
        setError(result.error || "Failed to fetch route corridors");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch route corridors");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRouteCorridors();
  }, [fetchRouteCorridors]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenForm = (routeCorridor = null) => {
    setEditRouteCorridor(routeCorridor);
    if (routeCorridor) {
      form.setFieldsValue(routeCorridor);
    } else {
      form.resetFields();
      form.setFieldsValue({
        active: true,
        provinces: [],
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditRouteCorridor(null);
    form.resetFields();
  };

  const handleSubmitForm = async (values) => {
    setFormLoading(true);
    try {
      let result;
      if (editRouteCorridor) {
        result = await updateRouteCorridor(editRouteCorridor.id, values);
      } else {
        result = await createRouteCorridor(values);
      }

      if (result.success) {
        message.success(
          `Route corridor ${editRouteCorridor ? "updated" : "created"} successfully`
        );
        handleCloseForm();
        fetchRouteCorridors();
      } else {
        message.error(result.error || "Failed to save route corridor");
      }
    } catch (err) {
      message.error(err.message || "Failed to save route corridor");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteRouteCorridor = async (routeCorridor) => {
    try {
      const result = await deleteRouteCorridor(routeCorridor.id);
      if (result.success) {
        message.success("Route corridor deleted successfully");
        fetchRouteCorridors();
      } else {
        message.error(result.error || "Failed to delete route corridor");
      }
    } catch (err) {
      message.error(err.message || "Failed to delete route corridor");
    }
  };

  const activeCount = routeCorridors.filter((r) => r.active).length;
  const inactiveCount = routeCorridors.filter((r) => !r.active).length;

  const headerActions = (
    <>
      {isMobile ? (
        <Tooltip title="Refresh">
          <IconButton
            onClick={fetchRouteCorridors}
            disabled={loading}
            aria-label="Refresh route corridors"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchRouteCorridors}
          disabled={loading}
        >
          Refresh
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => handleOpenForm()}
        sx={{
          flex: { xs: 1, md: "none" },
          textTransform: "none",
          fontWeight: 600,
          minHeight: { xs: 44, md: 36 },
        }}
      >
        {isMobile ? "Add Corridor" : "Add Route Corridor"}
      </Button>
    </>
  );

  return (
    <RequireAdmin>
      <PageShell>
        <PageHeader
          title="Route Corridors"
          back={{
            label: "Back to Trips",
            shortLabel: "Trips",
            onClick: () => navigate("/trips"),
          }}
          actions={headerActions}
        />

        <PageMetricStrip
          items={[
            {
              value: routeCorridors.length,
              label: "Total routes",
              shortLabel: "Total",
              color: "primary.main",
            },
            {
              value: activeCount,
              label: "Active routes",
              shortLabel: "Active",
              color: "success.main",
            },
            {
              value: inactiveCount,
              label: "Inactive routes",
              shortLabel: "Inactive",
              color: "error.main",
            },
          ]}
        />

        <PageFilters>
          <TextField
            placeholder="Search by name or description..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            size="small"
            fullWidth={isMobile}
            sx={{
              flexGrow: 1,
              maxWidth: { sm: 400 },
              bgcolor: "background.paper",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 150 },
              bgcolor: "background.paper",
            }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={
                filters.active === undefined ? "" : String(filters.active)
              }
              label="Status"
              onChange={(e) => {
                const value =
                  e.target.value === ""
                    ? undefined
                    : e.target.value === "true";
                handleFilterChange("active", value);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </PageFilters>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {loading && routeCorridors.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <RouteCorridorList
            routeCorridors={routeCorridors}
            loading={loading}
            onEdit={handleOpenForm}
            onDelete={handleDeleteRouteCorridor}
          />
        )}

        <Modal
          title={
            editRouteCorridor
              ? "Edit Route Corridor"
              : "Create New Route Corridor"
          }
          open={formOpen}
          onCancel={handleCloseForm}
          footer={null}
          width={isMobile ? "100%" : 600}
          style={
            isMobile ? { top: 16, maxWidth: "calc(100vw - 24px)" } : undefined
          }
          destroyOnHidden
        >
          <RouteCorridorForm
            form={form}
            onFinish={handleSubmitForm}
            onCancel={handleCloseForm}
            loading={formLoading}
            initialValues={editRouteCorridor || {}}
          />
        </Modal>
      </PageShell>
    </RequireAdmin>
  );
};

export default RouteCorridorsView;
